import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { referralFeesService, type SellerInviteOption } from "../../api/services/referralFees.service";
import { Field, Select } from "../ui";

/** PRD 6.2 AC2 / Master §4.3: the seller sees the agent's name only, never the fee terms */
export const inviteLabel = (o: SellerInviteOption): string => o.agentName;

interface PostUnderInviteSelectProps {
  value: string | null;
  onChange: (inviteId: string | null) => void;
}

/**
 * FR-08 / PRD 2.3 AC3 / 7.1: a seller may hold invites from several agents. The listing is posted
 * under one of them, and that agent's referral fee and first look bind to it.
 */
export function PostUnderInviteSelect({ value, onChange }: PostUnderInviteSelectProps) {
  const { data: invites = [] } = useQuery({
    queryKey: ["referral-fees", "seller-invites"],
    queryFn: () => referralFeesService.getSellerInvites(),
    staleTime: 60_000,
  });

  // The most recent invite is picked to start with; the seller can change it
  useEffect(() => {
    if (value === null && invites.length > 0) onChange(invites[0].inviteId);
  }, [invites, value, onChange]);

  if (invites.length === 0) return null;
  return (
    <Field
      label="Posting under an agent's invitation"
      htmlFor="referralInviteId"
      hint="Pick the agent who brought you to FlipIQ for this property."
    >
      <Select
        id="referralInviteId"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || "")}
        data-testid="post-under-invite"
      >
        {invites.map((o) => (
          <option key={o.inviteId} value={o.inviteId}>
            {inviteLabel(o)}
          </option>
        ))}
        <option value="">None of these agents</option>
      </Select>
    </Field>
  );
}
